import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Bold, Italic, Underline, Strikethrough, Highlighter, MessageSquarePlus, ChevronDown, ArrowUp, Type } from "lucide-react";
import { toHtml } from "../lib/richtext";

const SIZES = [["Petit", "2"], ["Normal", "3"], ["Grand", "5"]];
const HILITES = [["Rose", "#F3D2D9"], ["Doré", "#F5E6C8"], ["Aucun", "transparent"]];
const COLORS = [["Encre", "#2D181E"], ["Bordeaux", "#7B113A"], ["Bordeaux foncé", "#590224"], ["Gris", "#8A7A7F"]];

const keep = (e) => e.preventDefault();
const isTouch = () => typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

function TbBtn({ onDo, title, children, testid }) {
  return (
    <button type="button" title={title} data-testid={testid} onMouseDown={keep} onPointerDown={keep} onClick={onDo}
      className="flex h-8 items-center justify-center gap-0.5 rounded-full px-2 text-white/75 transition-colors hover:bg-white/10 hover:text-white">
      {children}
    </button>
  );
}

function Menu({ items, onPick, swatch }) {
  return (
    <div onMouseDown={keep} className="absolute left-0 top-10 z-10 min-w-[130px] overflow-hidden rounded-2xl border border-white/10 bg-[#2D181E] py-1 shadow-[0_12px_32px_rgba(45,24,30,0.35)]">
      {items.map(([label, v]) => (
        <button key={label} type="button" onMouseDown={keep} onPointerDown={keep} onClick={() => onPick(v)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-white/80 transition-colors hover:bg-white/10 hover:text-white">
          {swatch && <span className="h-3.5 w-3.5 rounded-full ring-1 ring-white/20" style={{ background: v === "transparent" ? "none" : v }} />}{label}
        </button>
      ))}
    </div>
  );
}

function CommentPop({ pos, onSend, onClose }) {
  const [text, setText] = useState("");
  const send = () => { if (text.trim()) onSend(text.trim()); };
  return (
    <div data-testid="comment-pop" onMouseDown={(e) => e.stopPropagation()} className="fixed z-[210] w-[300px] overflow-hidden rounded-2xl bg-white shadow-[0_16px_40px_rgba(45,24,30,0.25)]" style={{ top: pos.top, left: pos.left }}>
      <div className="flex items-center justify-between bg-[#2D181E] px-4 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F3D2D9]">Nouveau commentaire</p>
        <button type="button" onClick={onClose} className="text-xs text-white/50 hover:text-white">Annuler</button>
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2 rounded-full border-2 border-[#2D181E] py-1 pl-4 pr-1">
          <input autoFocus data-testid="comment-pop-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); if (e.key === "Escape") onClose(); }} placeholder="Ton commentaire…" className="flex-1 bg-transparent py-1.5 text-sm text-ink outline-none" />
          <button type="button" data-testid="comment-pop-send" onClick={send} disabled={!text.trim()} className="flex h-8 w-8 items-center justify-center rounded-full bg-bordeaux text-white shadow-[0_0_14px_rgba(123,17,58,0.5)] transition-opacity disabled:opacity-30"><ArrowUp size={15} /></button>
        </div>
      </div>
    </div>
  );
}

export function RichTextField({ initialHtml, value, onChange, onBlur, testid, fieldKey, onComment, placeholder = "", dark = false, className = "", readOnly = false }) {
  const ref = useRef(null);
  const saved = useRef(null);
  const [tb, setTb] = useState(null);
  const [menu, setMenu] = useState(null);
  const [pop, setPop] = useState(null);

  useEffect(() => { if (ref.current) ref.current.innerHTML = toHtml(initialHtml ?? value ?? ""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = () => onChange && ref.current && onChange(ref.current.innerHTML);

  useEffect(() => {
    const onSel = () => {
      const sel = window.getSelection();
      if (sel && ref.current && ref.current.contains(sel.anchorNode)) updateTb();
      else if (readOnly) { setTb(null); setMenu(null); }
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  });

  const updateTb = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !ref.current || !ref.current.contains(sel.anchorNode) || (readOnly && !onComment)) { setTb(null); setMenu(null); return; }
    const r = sel.getRangeAt(0).getBoundingClientRect();
    const w = readOnly ? 44 : onComment ? 330 : 290;
    const vv = window.visualViewport;
    const offY = vv ? vv.offsetTop : 0;
    const top = isTouch() ? Math.min(r.bottom + 14, (vv ? vv.height : window.innerHeight) - 52) + offY : Math.max(8, r.top - 52);
    setTb({ top, left: Math.max(8, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 8)), rect: r });
  };

  const exec = (c, v) => {
    ref.current.focus();
    try { document.execCommand("styleWithCSS", false, true); document.execCommand(c, false, v); } catch (_) {}
    setMenu(null); emit(); updateTb();
  };

  const openComment = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    saved.current = sel.getRangeAt(0).cloneRange();
    const r = tb.rect;
    setPop({ top: Math.min(r.bottom + 10, window.innerHeight - 140), left: Math.max(8, Math.min(r.right + 16, window.innerWidth - 316)) });
    setTb(null);
  };

  const sendComment = (text) => {
    const range = saved.current;
    const quote = range.toString();
    const id = "c" + Date.now() + Math.floor(Math.random() * 1000);
    const span = document.createElement("span");
    span.setAttribute("data-cid", id);
    try {
      range.surroundContents(span);
    } catch (_) {
      span.appendChild(range.extractContents());
      range.insertNode(span);
    }
    emit();
    setPop(null);
    onComment({ id, key: fieldKey, quote, text });
  };

  return (
    <div className="relative">
      {tb && createPortal(
        <div data-testid="rt-toolbar" onMouseDown={keep} onPointerDown={keep} className="fixed z-[200] flex items-center gap-0.5 rounded-full bg-[#2D181E] p-1 shadow-[0_12px_32px_rgba(45,24,30,0.35)]" style={{ top: tb.top, left: tb.left }}>
          {!readOnly && <>
          <div className="relative"><TbBtn title="Taille" testid="rt-size" onDo={() => setMenu(menu === "size" ? null : "size")}><Type size={14} /><ChevronDown size={11} /></TbBtn>{menu === "size" && <Menu items={SIZES} onPick={(v) => exec("fontSize", v)} />}</div>
          <span className="mx-0.5 h-5 w-px bg-white/15" />
          <TbBtn title="Gras" testid="rt-bold" onDo={() => exec("bold")}><Bold size={14} /></TbBtn>
          <TbBtn title="Italique" onDo={() => exec("italic")}><Italic size={14} /></TbBtn>
          <TbBtn title="Souligné" onDo={() => exec("underline")}><Underline size={14} /></TbBtn>
          <TbBtn title="Barré" onDo={() => exec("strikeThrough")}><Strikethrough size={14} /></TbBtn>
          <span className="mx-0.5 h-5 w-px bg-white/15" />
          <div className="relative"><TbBtn title="Surligner" testid="rt-hilite" onDo={() => setMenu(menu === "hl" ? null : "hl")}><Highlighter size={14} /><ChevronDown size={11} /></TbBtn>{menu === "hl" && <Menu swatch items={HILITES} onPick={(v) => exec("hiliteColor", v)} />}</div>
          <div className="relative"><TbBtn title="Couleur du texte" testid="rt-color" onDo={() => setMenu(menu === "co" ? null : "co")}><span className="text-sm font-semibold leading-none underline decoration-[#F3D2D9] decoration-2 underline-offset-2">A</span><ChevronDown size={11} /></TbBtn>{menu === "co" && <Menu swatch items={COLORS} onPick={(v) => exec("foreColor", v)} />}</div>
          </>}
          {onComment && <button type="button" title="Commenter" data-testid="btn-comment" onMouseDown={keep} onPointerDown={keep} onClick={openComment} className={`${readOnly ? "" : "ml-1 "}flex h-8 w-8 items-center justify-center rounded-full bg-bordeaux text-white shadow-[0_0_16px_rgba(123,17,58,0.6)] transition-transform hover:scale-105`}><MessageSquarePlus size={15} /></button>}
        </div>, document.body)}
      {pop && createPortal(<><div className="fixed inset-0 z-[205]" onMouseDown={() => setPop(null)} /><CommentPop pos={pop} onSend={sendComment} onClose={() => setPop(null)} /></>, document.body)}
      <div
        ref={ref}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        data-testid={testid}
        data-field={fieldKey}
        data-ph={placeholder}
        onInput={emit}
        onMouseUp={updateTb}
        onKeyUp={updateTb}
        onBlur={() => { if (onBlur) onBlur(ref.current.innerHTML); setTimeout(() => { if (!document.activeElement?.closest?.("[data-testid=rt-toolbar]")) { setTb(null); setMenu(null); } }, 150); }}
        className={`min-h-[46px] w-full whitespace-pre-wrap break-words outline-none transition-colors empty:before:pointer-events-none empty:before:content-[attr(data-ph)] [&_[data-cid]]:rounded-sm [&_[data-cid]]:bg-[#F3D2D9] [&_[data-cid]]:px-0.5 [&_[data-cid]]:text-ink ${dark ? "empty:before:text-white/30" : "empty:before:text-ink-muted/60"} ${className || "rounded-xl border border-line bg-white p-3 text-sm font-light leading-relaxed text-ink focus:border-bordeaux"}`}
      />
    </div>
  );
}
